/**
 * /tools/security-headers — public Security Headers Analyzer.
 */
import React, { useState } from "react";
import { motion } from "framer-motion";
import {
    Shield, ShieldAlert, Loader2, AlertTriangle, ChevronDown, ChevronUp,
    Search as SearchIcon, RefreshCw, FileText,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { postJSON } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";
import { ToolPageMeta } from "../components/ToolPageMeta";

const GRADE_COLORS = {
    A: "text-accent-mint",  B: "text-accent-cyan",
    C: "text-accent-amber", D: "text-accent-amber", F: "text-accent-red",
};

const FAQ = [
    {
        q: "What are security headers?",
        a: "HTTP response headers that instruct browsers how to protect users (CSP, HSTS, X-Frame-Options, etc.). They mitigate XSS, clickjacking, MIME-sniffing and a long list of common attacks. They are also explicit requirements in OWASP Top-10, PCI-DSS, and the NIS2 technical-measures checklist.",
    },
    {
        q: "How do I add a Content-Security-Policy?",
        a: "Start in Report-Only mode: `Content-Security-Policy-Report-Only: default-src 'self'; ...`. Collect violation reports, refine, then switch to enforcement. Tools like Report URI or CSP Evaluator help.",
    },
    {
        q: "Why does Permissions-Policy matter?",
        a: "It restricts which browser features a site (and its iframes) can use — camera, microphone, geolocation, accelerometer, etc. Add `Permissions-Policy: camera=(), microphone=(), geolocation=()` if your site doesn't need them.",
    },
];

const headerColor = (pts, max) => {
    if (pts === 0) return "border-accent-red/30 bg-accent-red/[0.04]";
    if (pts >= max) return "border-accent-mint/30 bg-accent-mint/[0.04]";
    return "border-accent-amber/30 bg-accent-amber/[0.04]";
};

export const SecurityHeaders = () => {
    usePageMeta({
        title: "Free Security Headers Analyzer — Cybersecurity Forefront",
        description: "Free HTTP security headers analyzer. CSP, HSTS, X-Frame-Options, Permissions-Policy and more — instant A-F grade with recommendations.",
        jsonLd: {
            "@context": "https://schema.org", "@type": "WebApplication",
            name: "Security Headers Analyzer", applicationCategory: "SecurityApplication",
            offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
        },
    });

    const [url, setUrl] = useState("");
    const [busy, setBusy] = useState(false);
    const [res, setRes] = useState(null);
    const [openRaw, setOpenRaw] = useState(false);
    const [openFaq, setOpenFaq] = useState(null);

    const submit = async (e) => {
        e?.preventDefault();
        let target = url.trim();
        if (!target.startsWith("http")) target = `https://${target}`;
        try { new URL(target); } catch { toast.error("Enter a valid URL"); return; }
        setBusy(true);
        setRes(null);
        try {
            const r = await postJSON("/tools/security-headers", { url: target });
            setRes(r);
        } catch (e) {
            const msg = e?.message || "Scan failed";
            if (msg.toLowerCase().includes("rate limit")) toast.error("Rate limit reached. Sign up free to unlock more scans.");
            else toast.error(msg);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="pt-24 md:pt-28 pb-16" data-testid="security-headers-page">
            <div className="max-w-3xl mx-auto px-5">
                <ToolPageMeta toolId="security-headers" />
                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">Free Tool</p>
                <h1 className="mt-2 font-display font-semibold text-4xl sm:text-5xl tracking-tight">
                    Score your HTTP security headers.
                </h1>
                <p className="mt-3 text-[14px] text-text-muted leading-relaxed max-w-xl">
                    Paste a URL — we send a single HTTPS request and grade what your server returns.
                    Anonymous lookups limited to 3/24h.
                </p>

                <form onSubmit={submit} className="mt-7 flex gap-2 flex-wrap" data-testid="sh-form">
                    <input type="text" value={url} onChange={(e) => setUrl(e.target.value)}
                        placeholder="https://example.com" data-testid="sh-url-input"
                        className="flex-1 min-w-[200px] px-4 py-3 bg-surface border border-white/[0.08] rounded-md text-[14px] focus:outline-none focus:border-accent-cyan" />
                    <button type="submit" disabled={busy} data-testid="sh-submit"
                        className="inline-flex items-center gap-1.5 px-5 py-3 rounded-md bg-accent-cyan text-canvas text-[13.5px] font-semibold hover:shadow-glow-cyan disabled:opacity-50">
                        {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Fetching…</> : <><SearchIcon className="w-4 h-4" /> Scan</>}
                    </button>
                </form>

                {res && (
                    <motion.section initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-10 space-y-4" data-testid="sh-results">
                        <div className="rounded-xl border border-white/[0.06] bg-surface/60 p-5 flex items-center gap-5 flex-wrap">
                            <div className={`font-display font-semibold text-5xl tabular-nums ${GRADE_COLORS[res.grade] || "text-text-primary"}`}>{res.score}</div>
                            <div className="flex-1 min-w-[200px]">
                                <p className="text-[11px] font-mono uppercase tracking-[0.22em] text-text-dim">URL · {res.status_code}</p>
                                <p className="font-mono text-accent-cyan text-[14px] truncate">{res.url}</p>
                                <p className="mt-2 text-[12.5px] text-text-muted">{res.summary}</p>
                            </div>
                            <button onClick={submit} className="text-[11px] inline-flex items-center gap-1 text-text-muted hover:text-accent-cyan">
                                <RefreshCw className="w-3 h-3" /> Re-scan
                            </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {res.sections.headers.map((h) => (
                                <div key={h.name} className={`rounded-lg border p-4 ${headerColor(h.pts_earned, h.max_pts)}`} data-testid={`sh-${h.key}`}>
                                    <div className="flex items-center justify-between flex-wrap gap-1">
                                        <p className="text-[13.5px] font-semibold">{h.name}</p>
                                        <span className="text-[11px] font-mono tabular-nums">{h.pts_earned}/{h.max_pts} pts</span>
                                    </div>
                                    <p className="mt-1 text-[11.5px] text-text-muted leading-relaxed">{h.description}</p>
                                    {h.present ? (
                                        <p className="mt-2 text-[11px] font-mono text-text-primary truncate">{h.value}</p>
                                    ) : (
                                        <p className="mt-2 text-[11px] font-mono text-accent-red">missing</p>
                                    )}
                                    {h.notes.length > 0 && (
                                        <p className="mt-1 text-[10.5px] font-mono text-text-dim">{h.notes.join(" · ")}</p>
                                    )}
                                </div>
                            ))}
                        </div>

                        {res.sections.warnings?.length > 0 && (
                            <div className="rounded-xl border border-accent-amber/30 bg-accent-amber/[0.05] p-4" data-testid="sh-warnings">
                                <div className="flex items-center gap-2 mb-2">
                                    <ShieldAlert className="w-4 h-4 text-accent-amber" />
                                    <h3 className="font-display font-semibold text-[14px] text-accent-amber">Information leaks</h3>
                                </div>
                                <ul className="space-y-1 text-[12.5px]">
                                    {res.sections.warnings.map((w, i) => <li key={i}>• {w}</li>)}
                                </ul>
                            </div>
                        )}

                        {res.recommendations?.length > 0 && (
                            <div className="rounded-xl border border-accent-cyan/30 bg-accent-cyan/[0.05] p-4" data-testid="sh-recommendations">
                                <h3 className="font-display font-semibold text-[14px] text-accent-cyan mb-2">Recommended next steps</h3>
                                <ul className="space-y-1.5 text-[12.5px]">
                                    {res.recommendations.map((r, i) => <li key={i}>• {r}</li>)}
                                </ul>
                            </div>
                        )}

                        <div className="rounded-xl border border-white/[0.06] bg-surface/40 overflow-hidden" data-testid="sh-raw-block">
                            <button onClick={() => setOpenRaw(!openRaw)} className="w-full flex items-center justify-between px-4 py-3 text-left">
                                <span className="text-[13.5px] font-medium inline-flex items-center gap-2">
                                    <FileText className="w-3.5 h-3.5 text-text-dim" /> Show all response headers
                                </span>
                                {openRaw ? <ChevronUp className="w-4 h-4 text-text-dim" /> : <ChevronDown className="w-4 h-4 text-text-dim" />}
                            </button>
                            {openRaw && (
                                <div className="px-4 pb-4 font-mono text-[11.5px] text-text-muted space-y-0.5 overflow-x-auto">
                                    {res.sections.all_headers.map((h, i) => (
                                        <div key={i}><span className="text-accent-cyan">{h.name}:</span> <span className="text-text-primary">{h.value}</span></div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </motion.section>
                )}

                <section className="mt-16 prose prose-invert max-w-none">
                    <h2 className="font-display font-semibold text-2xl tracking-tight">How HTTP security headers protect you</h2>
                    <p className="text-[14px] text-text-muted leading-relaxed mt-3">
                        Browsers respect a set of standardized HTTP response headers that turn off entire classes of attack. CSP
                        kills cross-site scripting. X-Frame-Options blocks clickjacking. Strict-Transport-Security forces HTTPS.
                        We grade the eight most-important headers on a weighted 0-100 scale and surface the exact line you need
                        to add to your reverse proxy.
                    </p>
                    <h3 className="mt-6 font-display font-semibold text-xl">Frequently asked questions</h3>
                    <div className="mt-3 space-y-2">
                        {FAQ.map((f, i) => (
                            <div key={i} className="rounded-md border border-white/[0.06] bg-surface/30">
                                <button type="button" onClick={() => setOpenFaq(openFaq === i ? null : i)}
                                    className="w-full flex items-center justify-between px-4 py-3 text-left">
                                    <span className="text-[13.5px] font-medium">{f.q}</span>
                                    {openFaq === i ? <ChevronUp className="w-4 h-4 text-text-dim" /> : <ChevronDown className="w-4 h-4 text-text-dim" />}
                                </button>
                                {openFaq === i && <div className="px-4 pb-3 text-[13px] text-text-muted">{f.a}</div>}
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </div>
    );
};

export default SecurityHeaders;
